import React from 'react';
import styles from './Tabs.module.css';
import Icon from './icons/Icons';

/**
 * Tabs
 * Props:
 * - items: Array<{ key: string, text: string, icon?: { name: string, width?: number, height?: number, color?: string } }>
 * - activeKey: string
 * - onChange: (key: string) => void
 * - size?: 'sm' | 'md' | 'lg' (default: 'md')
 * - className?: string
 * - trailing?: ReactNode rendered at the right end of the tab row
 */
export function Tabs({ items = [], activeKey, onChange, size = 'md', className, children, trailing }) {
  const sizeClass = size === 'lg' ? styles.lg : size === 'sm' ? styles.sm : styles.md;
  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <ul className={styles.list} role="tablist">
        {items.map((item) => {
          const isActive = item.key === activeKey;
          const btnClass = [styles.tab, sizeClass, isActive ? styles.active : null]
            .filter(Boolean)
            .join(' ');
          const icon = item && item.icon;
          const hasIcon = icon && typeof icon.name === 'string' && icon.name.length > 0;
          return (
            <li key={item.key} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                className={btnClass}
                onClick={() => onChange && onChange(item.key)}
              >
                {hasIcon ? (
                  <span className={styles.icon} aria-hidden>
                    <Icon
                      name={icon.name}
                      width={icon.width || 24}
                      height={icon.height || 24}
                      color={icon.color}
                    />
                  </span>
                ) : null}
                <span>{item.text}</span>
              </button>
            </li>
          );
        })}
        {trailing ? (
          <li className={styles.trailing} role="presentation">
            {trailing}
          </li>
        ) : null}
      </ul>
      {children ? (
        <div>
          {children}
        </div>
      ) : null}
    </div>
  );
}
